import { css } from '@emotion/react';
import { useMemo } from 'react';
import styleUtils from '../../styles/styleUtils';
import theme from '../../styles/theme';
import { borderTypes, sizes, } from './constants';
const arrowHorizontalSpacing = theme.spacing.x3;
const arrowVeritcalSpacing = {
    [sizes.medium]: theme.spacing.x4,
    [sizes.small]: theme.spacing.x3,
};
const errorHeight = 20;
const inputBorderWidth = 1;
const selectHeights = {
    [sizes.medium]: 48,
    [sizes.small]: 40,
};
const activeBorderColor = theme.colors.gray700;
const defaultBorderColor = theme.colors.gray400;
const borderStyles = {
    [borderTypes.bottom]: css `
    border-bottom-color: ${defaultBorderColor};
    border-left: 0;
    border-right: 0;
    border-top: 0;

    &:disabled {
      background: transparent;
    }

    &:focus {
      border-color: ${activeBorderColor};
      box-shadow: none;
    }
  `,
    [borderTypes.full]: css `
    border-color: ${defaultBorderColor};
  `,
    [borderTypes.none]: css `
    border-color: transparent;

    &:disabled {
      background: transparent;
    }

    &:focus {
      box-shadow: none;
    }
  `,
};
const disabledStyles = css `
  background: transparent;
  border-color: ${defaultBorderColor};
  color: ${theme.colors.gray600};
  opacity: 1;

  & ~ * {
    color: ${theme.colors.gray600};
  }
`;
const errorMessageStyles = css `
  ${theme.typeStyles.bodySmallStyles}
  color: ${theme.colors.negative800};
  margin-top: ${theme.spacing.base};
  width: 100%;
`;
const errorStyles = css `
  border-color: ${theme.colors.negative800};
`;
const optionStyles = css `
  color: ${theme.colors.gray700};
  cursor: pointer;
  padding-bottom: ${theme.spacing.x3};
  padding-left: ${theme.spacing.x3};
  padding-right: ${theme.spacing.x3};
  padding-top: ${theme.spacing.x3};

  &:hover {
    background-color: ${theme.colors.gray200};
  }

  &:disabled {
    color: ${theme.colors.gray500};
  }
`;
const placeholderStyles = css `
  color: ${theme.colors.gray600};
`;
const useErrorSpacingStyles = ({ size, }) => (useMemo(() => (css `
        min-height: ${styleUtils.rem(selectHeights[size] + errorHeight)};
      `), [
    size,
]));
const useIconContainerStyles = ({ size, }) => (useMemo(() => (css `
        pointer-events: none;
        position: absolute;
        right: ${arrowHorizontalSpacing};
        top: ${arrowVeritcalSpacing[size]};
      `), [
    size,
]));
const innerLabelVerticalOffsets = {
    [sizes.medium]: styleUtils.rem(6),
    [sizes.small]: theme.spacing.base,
};
const useInnerLabelStyles = ({ size, }) => (useMemo(() => (css `
        ${theme.typeStyles.bodyExtraSmallStyles}
        color: ${theme.colors.gray600};
        padding-left: calc(${theme.spacing.x3} + ${styleUtils.rem(inputBorderWidth)});
        padding-right: ${theme.spacing.x8};
        padding-top: calc(${innerLabelVerticalOffsets[size]} + ${styleUtils.rem(inputBorderWidth)});
        position: absolute;
        white-space: nowrap;
        width: 100%;
        pointer-events: none;
      `), [
    size,
]));
const useSelectContainerStyles = ({ disabled, size, }) => (useMemo(() => (css `
        background-color: ${theme.colors.gray100};
        height: ${styleUtils.rem(selectHeights[size])};
        overflow: hidden;
        position: relative;
        width: 100%;
        ${disabled && css `
          background-color: ${theme.colors.gray200};
        `}
      `), [
    disabled,
    size,
]));
const selectLabelVerticalOffsets = {
    [sizes.medium]: (styleUtils.rem(styleUtils.remToPxValue(theme.spacing.x4)
        + styleUtils.remToPxValue(theme.spacing.base))),
    [sizes.small]: theme.spacing.x4,
};
const selectVerticalSpacing = {
    [sizes.medium]: theme.spacing.x3,
    [sizes.small]: theme.spacing.x2,
};
const useSelectStyles = ({ borderType, disabled, error, isDefaultFont, label, size, touched, value, }) => (useMemo(() => (css `
        ${isDefaultFont && theme.typeStyles.bodyMediumStyles}
        appearance: none;
        background-color: transparent;
        border-color: ${defaultBorderColor};
        border-radius: 0;
        border-style: solid;
        border-width: ${styleUtils.rem(inputBorderWidth)};
        height: 100%;
        outline: 0;
        padding-bottom: ${theme.spacing.x3};
        padding-left: ${theme.spacing.x3};
        padding-right: ${theme.spacing.x8};
        padding-top: ${selectVerticalSpacing[size]};
        position: absolute;
        text-align: left;
        width: 100%;

        &:hover {
          border-color: ${theme.colors.gray500};
        }

        &:active,
        &:focus {
          border-color: ${activeBorderColor};
          outline: 0;
        }

        &:focus {
          box-shadow: 0 0 ${styleUtils.rem(3)} ${theme.colors.gray700};
        }

        &:disabled,
        &:disabled:hover {
          ${disabledStyles}
        }
        ${borderStyles[borderType]}
        ${label && css `
          padding-top: ${selectLabelVerticalOffsets[size]};
          padding-bottom: ${theme.spacing.base};
        `}
        ${!value && placeholderStyles}
        ${error && touched && errorStyles}
        ${disabled && disabledStyles}
      `), [
    borderType,
    disabled,
    error,
    isDefaultFont,
    label,
    size,
    touched,
    value,
]));
export { errorMessageStyles, optionStyles, useErrorSpacingStyles, useIconContainerStyles, useInnerLabelStyles, useSelectContainerStyles, useSelectStyles, };
